import * as THREE from 'three';
import type { Brush } from '../physics/Brush';
import type { CapsuleBody } from '../physics/CapsuleWorld';
import type { Input } from '../core/Input';
import type { PlayerController } from '../player/PlayerController';
import type { AudioManager } from '../audio/AudioManager';
import type { ArenaMats } from './ArenaMaterials';
import type { BreakFx } from './BreakFx';

export interface DoorDesc {
  id: string;
  /** Hinge world position (vertical center of the leaf). */
  hinge: THREE.Vector3;
  /** Closed yaw (Three.js rotation.y). Leaf extends along local +X. */
  closedYaw: number;
  /** +1 / −1 — open angle = openSign * π/2. */
  openSign: number;
  width: number;
  height: number;
  thickness: number;
  /** Metal WC stall leaf, or painted facility leaf (bath = blue). */
  style?: 'facility' | 'stall' | 'bath';
}

interface DoorRuntime {
  desc: DoorDesc;
  root: THREE.Group;
  brush: Brush;
  /** 0 = closed, 1 = fully open */
  openT: number;
  /** Target 0 or 1 */
  target: number;
  animating: boolean;
  destroyed: boolean;
}

const OPEN_SPEED = 2.6;
const INTERACT_DIST = 2.25;
const FACING_DOT = Math.cos((55 * Math.PI) / 180);

/**
 * Hinged doors: F to toggle, procedural leaf + handle, collision brush synced to swing.
 */
export class DoorSystem {
  private readonly doors: DoorRuntime[] = [];
  private readonly forward = new THREE.Vector3();
  private readonly toDoor = new THREE.Vector3();

  constructor(
    parent: THREE.Object3D,
    mats: ArenaMats,
    descs: DoorDesc[],
    private readonly brushes: Brush[],
    private readonly audio: AudioManager,
  ) {
    for (const desc of descs) {
      const brush = createDoorBrush(desc);
      this.brushes.push(brush);

      const root = createDoorMesh(desc, mats);
      root.position.copy(desc.hinge);
      root.rotation.y = desc.closedYaw;
      parent.add(root);

      this.doors.push({
        desc,
        root,
        brush,
        openT: 0,
        target: 0,
        animating: false,
        destroyed: false,
      });
      syncDoorPose(this.doors[this.doors.length - 1]!);
    }
  }

  /** After vertex bake: force leftover live-lit leaf/hardware to unlit Basic. */
  sealAfterBake(): void {
    for (const d of this.doors) {
      d.root.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (!mesh.isMesh) return;
        const list = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        const next = list.map((m) => sealDoorMaterial(m));
        mesh.material = Array.isArray(mesh.material) ? next : next[0]!;
        mesh.castShadow = false;
        mesh.receiveShadow = false;
      });
    }
  }

  blastAt(origin: THREE.Vector3, radius: number, fx: BreakFx): void {
    for (const d of this.doors) {
      if (d.destroyed) continue;
      const mid = doorMid(d);
      if (mid.distanceTo(origin) > radius + 0.35) continue;
      d.destroyed = true;
      d.animating = false;
      d.brush.noCollision = true;
      d.root.visible = false;
      d.root.traverse((o) => {
        o.userData.ignoreHit = true;
      });
      fx.spawnChunks(
        mid,
        new THREE.Vector3(d.desc.width, d.desc.height, Math.max(0.12, d.desc.thickness * 4)),
        0x3d7a4f,
        16,
        'rubble',
        d.desc.hinge.y - d.desc.height * 0.5,
      );
      this.audio.playAt('lampBreak', mid, undefined, { volume: 0.55, rate: 0.85 + Math.random() * 0.2, range: 36 });
    }
  }

  update(player: PlayerController, input: Input, dt: number, allowInteract = true): void {
    for (const d of this.doors) {
      if (d.destroyed) continue;
      if (d.animating) {
        const dir = Math.sign(d.target - d.openT) || 0;
        if (dir === 0) {
          d.animating = false;
          d.brush.noCollision = false;
        } else {
          d.openT += dir * OPEN_SPEED * dt;
          if ((dir > 0 && d.openT >= d.target) || (dir < 0 && d.openT <= d.target)) {
            d.openT = d.target;
            d.animating = false;
            d.brush.noCollision = false;
          } else {
            d.brush.noCollision = true;
          }
          syncDoorPose(d);
        }
      } else if (d.brush.noCollision) {
        // Unstick failed (wedged in a jamb) — stay ghosted until you walk out.
        d.brush.noCollision = bodyHitsDoor(player.body, d);
      }
    }

    if (!allowInteract || !input.interactPressed) return;

    const best = this.pickDoor(player);
    if (!best || best.animating) return;

    best.target = best.target > 0.5 ? 0 : 1;
    best.animating = true;
    best.brush.noCollision = true;
    const opening = best.target > 0.5;
    this.audio.play('doorOpen', {
      volume: opening ? 0.55 : 0.45,
      rate: opening ? 0.95 + Math.random() * 0.1 : 1.15 + Math.random() * 0.15,
    });
  }

  /** NPCs push a closed door open when they walk up to it. */
  openNear(pos: THREE.Vector3, radius = 1.85): boolean {
    let best: DoorRuntime | null = null;
    let bestD = radius;
    for (const d of this.doors) {
      if (d.destroyed || d.target > 0.5) continue;
      const mid = doorMid(d);
      const dist = Math.hypot(mid.x - pos.x, mid.z - pos.z);
      if (dist < bestD) {
        bestD = dist;
        best = d;
      }
    }
    if (!best) return false;
    this.beginOpen(best);
    return true;
  }

  openById(id: string): boolean {
    if (!id) return false;
    const d = this.doors.find((x) => x.desc.id === id);
    if (!d || d.destroyed || d.target > 0.5) return false;
    this.beginOpen(d);
    return true;
  }

  private beginOpen(d: DoorRuntime): void {
    d.target = 1;
    d.animating = true;
    d.brush.noCollision = true;
    const mid = doorMid(d);
    this.audio.playAt('doorOpen', mid, undefined, {
      volume: 0.42,
      rate: 0.95 + Math.random() * 0.1,
      range: 18,
    });
  }

  anyAnimating(): boolean {
    for (const d of this.doors) {
      if (d.animating) return true;
    }
    return false;
  }

  isAjar(id: string): boolean {
    const d = this.doors.find((x) => x.desc.id === id);
    if (!d) return false;
    return d.destroyed || d.target > 0.5 || d.openT > 0.12;
  }

  /** Closed vs open leaves for hearing — stall bays and room portals. */
  hearDoors(): Array<{
    id: string;
    style: NonNullable<DoorDesc['style']>;
    width: number;
    hinge: THREE.Vector3;
    mid: THREE.Vector3;
    ajar: boolean;
  }> {
    return this.doors.map((d) => ({
      id: d.desc.id,
      style: d.desc.style ?? 'facility',
      width: d.desc.width,
      hinge: d.desc.hinge,
      mid: doorMid(d),
      ajar: d.destroyed || d.target > 0.5 || d.openT > 0.12,
    }));
  }

  lookTip(
    player: PlayerController,
    camera: THREE.Camera,
    width: number,
    height: number,
  ): { label: string; x: number; y: number } | null {
    const d = this.pickDoor(player);
    if (!d || d.animating || width < 8 || height < 8) return null;
    const mid = doorMid(d);
    mid.y += d.desc.height * 0.22;
    this.toDoor.copy(mid).project(camera);
    if (this.toDoor.z < -1 || this.toDoor.z > 1) return null;
    const open = d.target > 0.5 || d.openT > 0.55;
    return {
      label: open ? 'F - Close Door' : 'F - Open Door',
      x: Math.min(width - 40, Math.max(40, (this.toDoor.x * 0.5 + 0.5) * width)),
      y: Math.min(height - 24, Math.max(22, (-this.toDoor.y * 0.5 + 0.5) * height)),
    };
  }

  private pickDoor(player: PlayerController): DoorRuntime | null {
    const pos = player.body.position;
    this.forward.set(-Math.sin(player.yaw), 0, -Math.cos(player.yaw));

    let best: DoorRuntime | null = null;
    let bestScore = Infinity;

    for (const d of this.doors) {
      if (d.destroyed) continue;
      // Interact point: mid-leaf when closed-ish, else hinge
      const ang = d.desc.closedYaw + d.openT * d.desc.openSign * (Math.PI * 0.5);
      const half = d.desc.width * 0.5;
      const c = Math.cos(ang);
      const s = Math.sin(ang);
      const midX = d.desc.hinge.x + c * half;
      const midZ = d.desc.hinge.z - s * half;

      if (Math.abs(pos.y - d.desc.hinge.y) > 1.7) continue;
      this.toDoor.set(midX - pos.x, 0, midZ - pos.z);
      const dist = this.toDoor.length();
      if (dist > INTERACT_DIST) continue;
      if (dist > 1e-4) this.toDoor.multiplyScalar(1 / dist);
      const facing = this.forward.dot(this.toDoor);
      if (facing < FACING_DOT && dist > 0.85) continue;

      const score = dist - facing * 0.35;
      if (score < bestScore) {
        bestScore = score;
        best = d;
      }
    }
    return best;
  }
}

function bodyHitsDoor(body: CapsuleBody, d: DoorRuntime): boolean {
  const brush = d.brush;
  const pad = body.radius + 0.02;
  const hy = brush.size.y * 0.5;
  if (body.position.y + body.height < brush.center.y - hy || body.position.y > brush.center.y + hy) {
    return false;
  }
  const hx = brush.size.x * 0.5;
  const hz = brush.size.z * 0.5;
  const dx = body.position.x - brush.center.x;
  const dz = body.position.z - brush.center.z;
  const c = Math.cos(brush.yaw);
  const s = Math.sin(brush.yaw);
  const lx = c * dx - s * dz;
  const lz = s * dx + c * dz;
  return Math.abs(lx) < hx + pad && Math.abs(lz) < hz + pad;
}

function doorMid(d: DoorRuntime): THREE.Vector3 {
  const ang = d.desc.closedYaw + d.openT * d.desc.openSign * (Math.PI * 0.5);
  const half = d.desc.width * 0.5;
  return new THREE.Vector3(
    d.desc.hinge.x + Math.cos(ang) * half,
    d.desc.hinge.y,
    d.desc.hinge.z - Math.sin(ang) * half,
  );
}

function syncDoorPose(d: DoorRuntime): void {
  const yaw = d.desc.closedYaw + d.openT * d.desc.openSign * (Math.PI * 0.5);
  const leaf = d.root.userData.swingLeaf as THREE.Object3D | undefined;
  if (leaf) {
    d.root.rotation.y = d.desc.closedYaw;
    leaf.rotation.y = d.openT * d.desc.openSign * (Math.PI * 0.5);
  } else {
    d.root.rotation.y = yaw;
  }

  const half = d.desc.width * 0.5;
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  // Three.js yaw: x' = c*x + s*z, z' = -s*x + c*z — local center (half,0,0)
  d.brush.center.set(
    d.desc.hinge.x + c * half,
    d.desc.hinge.y,
    d.desc.hinge.z - s * half,
  );
  d.brush.yaw = yaw;
}

function createDoorBrush(desc: DoorDesc): Brush {
  const half = desc.width * 0.5;
  const c = Math.cos(desc.closedYaw);
  const s = Math.sin(desc.closedYaw);
  return {
    id: `door_leaf_${desc.id}`,
    kind: 'box',
    center: new THREE.Vector3(
      desc.hinge.x + c * half,
      desc.hinge.y,
      desc.hinge.z - s * half,
    ),
    size: new THREE.Vector3(desc.width, desc.height, desc.thickness),
    yaw: desc.closedYaw,
    material: { color: 0x4a3c30, roughness: 0.85, metalness: 0.05 },
    // Visual leaf comes from createDoorMesh — brush is collision only
    invisible: true,
  };
}

function createDoorMesh(desc: DoorDesc, mats: ArenaMats): THREE.Group {
  if (desc.style === 'stall') return createStallDoorMesh(desc, mats);
  const root = new THREE.Group();
  root.name = `door_${desc.id}`;

  const w = desc.width;
  const h = desc.height;
  const t = desc.thickness;
  const bath = desc.style === 'bath';
  const paint = (bath ? mats.bathDoor : mats.door).clone();
  const chrome = unlitChrome(mats.chrome);

  const leaf = new THREE.Group();
  leaf.position.set(w * 0.5, 0, 0);

  const core = new THREE.Mesh(new THREE.BoxGeometry(w, h, t), paint);
  core.castShadow = true;
  core.receiveShadow = true;
  leaf.add(core);

  addChromeHandle(leaf, w, t, 1, chrome);
  addChromeHandle(leaf, w, t, -1, chrome);

  for (const hy of [-h * 0.35, 0, h * 0.35]) {
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.022, 0.11, 10), chrome);
    barrel.position.set(-w * 0.5 + 0.018, hy, 0);
    leaf.add(barrel);
  }

  root.add(leaf);
  return root;
}

function createStallDoorMesh(desc: DoorDesc, mats: ArenaMats): THREE.Group {
  const root = new THREE.Group();
  root.name = `door_${desc.id}`;
  const w = desc.width;
  const h = desc.height;
  const t = desc.thickness;
  const panel = new THREE.MeshStandardMaterial({
    color: 0x5a626c,
    roughness: 0.48,
    metalness: 0.22,
  });
  const hardware = unlitChrome(mats.chrome);

  // Leaf origin is the hinge so the booth plates on `root` stay put when it swings.
  const leaf = new THREE.Group();
  leaf.name = 'swing_leaf';
  const core = new THREE.Mesh(new THREE.BoxGeometry(w, h, t), panel);
  core.position.set(w * 0.5, 0, 0);
  core.castShadow = true;
  core.receiveShadow = true;
  leaf.add(core);

  const latch = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.045, t + 0.03), hardware);
  latch.position.set(w * 0.5 + w * 0.32, 0.12, 0);
  leaf.add(latch);
  for (const zSign of [-1, 1]) {
    const handle = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.016, 0.02), hardware);
    handle.position.set(w * 0.5 + w * 0.3, 0.02, zSign * (t * 0.5 + 0.016));
    leaf.add(handle);
  }

  const jamb = new THREE.Group();
  jamb.name = 'stall_jamb';
  for (const hy of [-h * 0.32, h * 0.32]) addStallHinge(leaf, jamb, hy, t, hardware);

  root.add(leaf);
  root.add(jamb);
  root.userData.swingLeaf = leaf;
  return root;
}

/** Door strap + knuckle on the leaf; pilaster strap + pin stay on the booth. */
function addStallHinge(
  leaf: THREE.Group,
  jamb: THREE.Group,
  hy: number,
  t: number,
  hardware: THREE.Material,
): void {
  const doorFace = t * 0.5 + 0.008;
  const doorStrap = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.085, 0.012), hardware);
  doorStrap.position.set(0.048, hy, doorFace);
  leaf.add(doorStrap);

  const knuckle = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.016, 0.058, 10), hardware);
  knuckle.position.set(0, hy, 0);
  leaf.add(knuckle);

  // Left pilaster sits ~7 cm in −X and 3.5 cm in −Z from the hinge.
  const jambStrap = new THREE.Mesh(new THREE.BoxGeometry(0.058, 0.085, 0.012), hardware);
  jambStrap.position.set(-0.04, hy, -0.01);
  jamb.add(jambStrap);

  const jambFront = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.085, 0.055), hardware);
  jambFront.position.set(-0.066, hy, -0.03);
  jamb.add(jambFront);

  const pin = new THREE.Mesh(new THREE.CylinderGeometry(0.007, 0.007, 0.11, 8), hardware);
  pin.position.set(0, hy, 0);
  jamb.add(pin);
}

function unlitChrome(src: THREE.MeshStandardMaterial): THREE.MeshStandardMaterial {
  const m = src.clone();
  m.emissive.setHex(0);
  m.emissiveIntensity = 0;
  return m;
}

function sealDoorMaterial(mat: THREE.Material): THREE.Material {
  if (!(mat instanceof THREE.MeshStandardMaterial)) return mat;
  if (mat.emissiveIntensity > 0.18 && mat.emissive && mat.emissive.getHex() > 0) return mat;
  return new THREE.MeshBasicMaterial({
    color: mat.color.clone(),
    map: mat.map,
    vertexColors: true,
    fog: true,
    transparent: mat.transparent,
    opacity: mat.opacity,
    side: mat.side,
    alphaTest: mat.alphaTest,
  });
}

function addChromeHandle(
  leaf: THREE.Group,
  w: number,
  t: number,
  zSign: number,
  chrome: THREE.Material,
): void {
  const g = new THREE.Group();
  const z = zSign * (t * 0.5 + 0.02);
  g.position.set(w * 0.32, 0, z);

  const rose = new THREE.Mesh(new THREE.CylinderGeometry(0.048, 0.048, 0.018, 12), chrome);
  rose.rotation.x = Math.PI * 0.5;
  g.add(rose);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.016, 0.05, 8), chrome);
  neck.rotation.x = Math.PI * 0.5;
  neck.position.z = zSign * 0.03;
  g.add(neck);

  const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.013, 0.013, 0.17, 8), chrome);
  bar.rotation.z = Math.PI * 0.5;
  bar.position.set(-0.055, 0, zSign * 0.052);
  g.add(bar);

  const tip = new THREE.Mesh(new THREE.SphereGeometry(0.018, 8, 8), chrome);
  tip.position.set(-0.14, 0, zSign * 0.052);
  g.add(tip);

  leaf.add(g);
}
